import { computed, ref } from 'vue'

export interface ChartIconPath {
  path: string
  color: string
}

export interface ChartIconConfig {
  name: string
  svgPath?: string
  paths?: ChartIconPath[]
  viewBox: string
  color?: string
}

// SVG icon registry - this will be populated by the components themselves
const iconRegistry = ref<Record<string, ChartIconConfig>>({})

/**
 * Composable for rendering SVG icons in Chart.js labels
 * Uses a registry pattern to automatically sync with SVG components
 */
export function useChartIcons() {
  // Register an icon in the global registry
  const registerIcon = (key: string, config: ChartIconConfig) => {
    iconRegistry.value[key] = config
  }

  // Get all registered icons
  const chartIcons = computed((): Record<string, ChartIconConfig> => {
    return iconRegistry.value
  })

  // Generate HTML string for SVG icon
  const getIconHTML = (iconName: string, size: number = 16): string => {
    const icon = chartIcons.value[iconName]
    if (!icon) return ''

    return `<svg width="${size}" height="${size}" viewBox="${icon.viewBox}" fill="${icon.color || 'currentColor'}" style="display: inline-block; vertical-align: middle; margin-right: 4px;">
      <path d="${icon.svgPath}"/>
    </svg>`
  }

  // Get emoji fallback for icon
  const getEmojiFallback = (iconName: string): string => {
    const emojiMap: Record<string, string> = {
      'SocialFacebook': '📘',
      'SocialTwitter': '🐦',
      'SocialX': '🐦',
      'SocialLinkedIn': '💼',
      'SocialInstagram': '📷',
      'SocialSMS': '📱',
      'SocialSlack': '💬',
      'SocialLine': '💚',
      'SocialEmail': '📧',
      'SocialWhatsApp': '📱',
      'SocialTelegram': '📱',
      'SocialBluesky': '🐦',
      'SocialXing': '💼',
    }
    
    return emojiMap[iconName] || '📊'
  }

  // Check if icon supports HTML rendering
  const supportsHTML = (iconName: string): boolean => {
    return iconName in chartIcons.value
  }

  return {
    chartIcons,
    registerIcon,
    getIconHTML,
    getEmojiFallback,
    supportsHTML
  }
} 